import React, { useEffect, useState, useMemo } from "react";

import { Dialog, Portal, SimpleGrid, Select, HStack, CloseButton, Button, useSelectContext, createListCollection, Text, Field, Input, ColorPicker, parseColor } from "@chakra-ui/react";
import { getGeojsonPropertiesKey, rgbaToHex } from "../../../../../utilies/Utility";
import { toggleLabel } from "../../../../../utilies/geojson/Label";
import { useAppStore } from "../../../../../context/store";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
const LayersLabelDialog = () => {
  // state
  const showLabelDialog = useAppStore((s) => s.showLabelDialog);
  const currentLayer = useAppStore((s) => s.currentLayer);

  // setters / refs
  const setShowLabelDialog = useAppStore.getState().setShowLabelDialog;
  const setCurrentLayer = useAppStore.getState().setCurrentLayer;
  const { setMapObjects } = useMapObjects();
  const viewerRef = useAppStore.getState().viewerRef;

  const [labelPropertiesOption, setLabelPropertiesOption] = useState([]);
  const [selectedValue, setSelectedValue] = useState([]);
  const [textColor, setTextColor] = useState("#000000");
  const [textSize, setTextSize] = useState(13);
  const [offset, setOffset] = useState(2);

  const TypeSelectValue = () => {
    const select = useSelectContext();
    const items = select && select.selectedItems ? select.selectedItems : [];
    if (items[0]) {
      return (
        <Select.ValueText placeholder="Select member">
          <HStack>{items[0].name}</HStack>
        </Select.ValueText>
      );
    }
    return null;
  };

  const labelCollection = useMemo(
    () =>
      createListCollection({
        items: labelPropertiesOption,
        itemToString: (item) => (item && item.name != null ? String(item.name) : ""),
        itemToValue: (item) => (item && item.name != null ? String(item.name) : ""),
      }),
    [labelPropertiesOption]
  );

  const parsedTextColor = useMemo(() => parseColor(textColor), [textColor]);

  useEffect(() => {
    if (!showLabelDialog) return;

    if (!currentLayer) {
      setLabelPropertiesOption([]);
      setSelectedValue([]);
      return;
    }

    const previewLabel = getGeojsonPropertiesKey(currentLayer.geojson) || {};

    const safeSelected =
      Array.isArray(currentLayer?.selected_tags_for_label) && currentLayer.selected_tags_for_label.length ? currentLayer.selected_tags_for_label.filter(Boolean).map((v) => String(v)) : [];
    setSelectedValue(safeSelected);

    const temp = [];
    for (const key in previewLabel) {
      const val = previewLabel[key];
      if (val !== null && val !== undefined && String(val).trim() !== "") {
        temp.push({ name: String(val) });
      }
    }
    setLabelPropertiesOption(temp);

    setTextColor(currentLayer.label_text_color || "#000000");
    setTextSize(currentLayer.label_text_size ?? 15);
    setOffset(currentLayer.label_offset ?? 2);
  }, [currentLayer, showLabelDialog]);

  const showPreviewLabels = (arr) => {
    if (!arr || !Array.isArray(arr)) return "";
    return arr.filter(Boolean).map(String).join(" - ");
  };

  const handleSave = async () => {
    setShowLabelDialog(false);

    try {
      const entities = currentLayer?.dataSource?.entities?.values;
      if (viewerRef?.current && entities) {
        toggleLabel({
          viewer: viewerRef.current,
          dataSource: currentLayer?.dataSource,
          keys: selectedValue,
          visible: true,
          textSize: textSize,
          textColor: textColor,
          show: true,
          offset: offset,
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("rebuildBillboards failed:", e);
    }

    setMapObjects((prev) =>
      prev.map((obj) =>
        obj?.id === currentLayer?.id
          ? {
              ...obj,
              selected_tags_for_label: Array.isArray(selectedValue) ? selectedValue.slice() : [],
              label_text_color: textColor,
              label_text_size: textSize,
              label_visible: true,
              label_offset: offset,
              is_edited: true,
            }
          : obj
      )
    );
  };

  if (showLabelDialog && !currentLayer) {
    return null;
  }

  return (
    <Dialog.Root
      lazyMount
      open={showLabelDialog}
      onOpenChange={(e) => {
        setShowLabelDialog(Boolean(e.open));
        if (!Boolean(e.open)) {
          setCurrentLayer(null);
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Label Style</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <SimpleGrid columns={1} gap={1}>
                <Select.Root
                  multiple
                  collection={labelCollection}
                  size="sm"
                  value={selectedValue}
                  positioning={{ sameWidth: true }}
                  onValueChange={(value) => {
                    const val = value && value.value;
                    let temp = [];
                    if (Array.isArray(val)) {
                      temp = val;
                    } else if (val instanceof Set) {
                      temp = Array.from(val);
                    } else if (val && typeof val === "object") {
                      try {
                        temp = Object.values(val);
                      } catch {
                        temp = [];
                      }
                    }
                    temp = temp.filter(Boolean).map((v) => String(v));

                    if (temp.length > 2) {
                      return;
                    }

                    setSelectedValue(temp);
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Label>Fields</Select.Label>

                  <Select.Control>
                    <Select.Trigger>
                      <TypeSelectValue />
                    </Select.Trigger>

                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>

                  <Select.Positioner>
                    <Select.Content>
                      {labelPropertiesOption.map(
                        (item) =>
                          item && (
                            <Select.Item item={item} key={item.name} justifyContent="flex-start">
                              {item.name}
                              <Select.ItemIndicator />
                            </Select.Item>
                          )
                      )}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>

                <HStack>
                  <Text fontSize={"16px"} mr={3}>
                    Pattern:
                  </Text>
                  <Text fontSize={"18px"}>{showPreviewLabels(selectedValue)}</Text>
                </HStack>

                <HStack gap={3} mt={3}>
                  <ColorPicker.Root
                    width={"1/2"}
                    value={parsedTextColor}
                    onValueChange={(v) => {
                      const newColor = rgbaToHex(v.valueAsString);
                      setTextColor(newColor);

                      const update = () => setMapObjects((prev) => prev.map((obj) => (obj?.id === currentLayer?.id ? { ...obj, label_text_color: newColor } : obj)));

                      if (React.startTransition) {
                        React.startTransition(update);
                      } else {
                        update();
                      }
                    }}
                  >
                    <ColorPicker.HiddenInput />
                    <ColorPicker.Label>Text Color</ColorPicker.Label>
                    <ColorPicker.Control>
                      <ColorPicker.Input />
                      <ColorPicker.Trigger />
                    </ColorPicker.Control>
                    <ColorPicker.Positioner>
                      <ColorPicker.Content>
                        <ColorPicker.Area />
                        <HStack>
                          <ColorPicker.EyeDropper size="xs" variant="outline" />
                          <ColorPicker.Sliders />
                        </HStack>
                      </ColorPicker.Content>
                    </ColorPicker.Positioner>
                  </ColorPicker.Root>

                  <Field.Root width={"1/2"}>
                    <Field.Label>Text Size</Field.Label>
                    <Input
                      placeholder="Enter Text Size"
                      type="number"
                      value={textSize}
                      onChange={(evt) => {
                        const newSize = parseInt(evt.target.value, 10);
                        if (!Number.isNaN(newSize)) {
                          setTextSize(newSize);

                          const update = () => setMapObjects((prev) => prev.map((obj) => (obj?.id === currentLayer?.id ? { ...obj, label_text_size: newSize } : obj)));

                          if (React.startTransition) {
                            React.startTransition(update);
                          } else {
                            update();
                          }
                        }
                      }}
                    />
                  </Field.Root>
                </HStack>
                <Field.Root width={"1/2"}>
                  <Field.Label>Offset</Field.Label>
                  <Input
                    placeholder="Enter offset"
                    type="number"
                    value={offset}
                    onChange={(evt) => {
                      const newOffset = parseInt(evt.target.value, 10);
                      if (!Number.isNaN(newOffset)) {
                        setOffset(newOffset);

                        const update = () => setMapObjects((prev) => prev.map((obj) => (obj?.id === currentLayer?.id ? { ...obj, label_offset: newOffset } : obj)));

                        if (React.startTransition) {
                          React.startTransition(update);
                        } else {
                          update();
                        }
                      }
                    }}
                  />
                </Field.Root>
              </SimpleGrid>
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline" onClick={() => setShowLabelDialog(false)}>
                  Cancel
                </Button>
              </Dialog.ActionTrigger>

              <Button onClick={handleSave}>Save</Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default LayersLabelDialog;
